import { Component, Input, OnInit, untracked, ChangeDetectionStrategy } from "@angular/core";
// HighCharts
import * as Highcharts from "highcharts/highstock";
import bullet from "highcharts/modules/bullet";
bullet(Highcharts); // Translate
import { TranslateService } from "@ngx-translate/core";
// Own services
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
// Variables
import { GRAPH_CONFIG } from "../GRAPH_CONFIG";
import { HighchartsChartModule } from "highcharts-angular";
import { THEME_COLORS } from "../../../themes/THEME_COLORS";

/**
 * Highcharts bullet chart built from `data.series` and `data.plotBands`, themed with the session colors.
 * Rebuilds when `data` or `width` changes; `height` sets the chart height.
 */
@Component({
  selector: "app-graph-bullet",
  templateUrl: "./graph-bullet.component.html",
  styleUrls: ["./graph-bullet.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HighchartsChartModule]
})
export class GraphBulletComponent implements OnInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Chart
  @Input()
  get data(): any {
    return this._data;
  }
  set data(data: any) {
    this._data = data;
    if (this._data && this._width) {
      this.chartOptions = null;
      setTimeout(() => this.setChartOptions(), 0);
    }
  }
  _data: any;
  @Input()
  get width(): any {
    return this._width;
  }
  set width(width: any) {
    this._width = width;
    if (this._data && this._width) {
      this.chartOptions = null;
      setTimeout(() => this.setChartOptions(), 0);
    }
  }
  _width: number;
  @Input() height: number;
  Highcharts: typeof Highcharts = Highcharts;
  updateFlag: boolean = false;
  chartConstructor: string = "stockChart";
  chartOptions: any;
  options: any = {};
  currentColors: object;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly SessionDataService: SessionDataService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentColors = untracked(() => this.SessionDataService.colors());

    // Adjust chart options
    let defaultOptions: any = JSON.parse(JSON.stringify(GRAPH_CONFIG.default.options));

    for (let attribute in defaultOptions.lang) {
      if (defaultOptions.lang[attribute].constructor === Array) {
        defaultOptions.lang[attribute] = defaultOptions.lang[attribute].map((data: string) => {
          return this.translate.instant(data);
        });
      } else {
        defaultOptions.lang[attribute] = this.translate.instant(defaultOptions.lang[attribute]);
      }
    }

    this.options = {
      accessibility: defaultOptions.accessibility,
      lang: defaultOptions.lang,
      tooltip: {
        outside: true
      }
    };

    Highcharts.setOptions(this.options);
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Set chart options
  setChartOptions(): void {
    this.chartOptions = {
      chart: {
        animate: false,
        backgroundColor: "transparent",
        borderWidth: 0,
        width: this._width,
        height: this.height ? this.height : 70,
        style: {
          overflow: "visible"
        },
        inverted: true,
        type: "bullet"
      },
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      xAxis: {
        visible: false
      },
      yAxis: {
        labels: {
          enabled: false
        },
        title: null,
        gridLineWidth: 0,
        plotBands: this._data.plotBands
      },
      legend: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        series: {
          grouping: false,
          pointPadding: 0,
          borderWidth: 5,
          borderColor: this.currentColors[THEME_COLORS.GRAY_DARK],
          borderRadius: 0,
          color: this.currentColors[THEME_COLORS.GRAY_DARK],
          targetOptions: {
            color: "black",
            width: "200%"
          },
          enableMouseTracking: false
        }
      },
      series: this._data.series
    };

    this.updateFlag = true;
  }
}
